import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UpdateAuthUser } from '@myStore/auth/auth.actions';
import { EditSelfProfileRequest } from '@myStore/profile-changes/profile-changes.actions';
import { GetProfileRequest } from '@myStore/profile-view/profile-view.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ProfileService } from '@services/profile/profile.service';
import { UsersService } from '@services/users/users.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, map, switchMap } from 'rxjs/operators';
import { IAppState } from '../app.state';
import {
  ChangePassword,
  ChangePasswordFailure,
  ChangePasswordSuccess,
  EditSelfProfile,
  EditSelfProfileFailure,
  EProfileActions,
  GetEmailTokenValidity,
  VerifyEmailFetch,
  VerifyEmailFetchFailure,
  VerifyEmailRequest,
  VerifyEmailRequestSuccess,
  VerifyPhone,
  VerifyPhoneRequest,
  VerifyPhoneRequestFailure,
  VerifyPhoneRequestSuccess,
} from './profile.actions';

@Injectable()
export class ProfileEffects {
  constructor(
    private _actions$: Actions,
    private _store: Store<IAppState>,
    private _profileService: ProfileService,
    private _usersService: UsersService,
    private _router: Router,
    private _toastr: ToastrService
  ) {}

  verifyPhoneRequest$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<VerifyPhoneRequest>(EProfileActions.VerifyPhoneRequest),
        switchMap(() => {
          return this._profileService.verifyPhoneRequest().pipe(
            map(res => {
              this._store.dispatch(new VerifyPhoneRequestSuccess(res.detail));
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При верификации phone возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new VerifyPhoneRequestFailure(err.error));
              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  verifyEmailRequest$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<VerifyEmailRequest>(EProfileActions.VerifyEmailRequest),
        switchMap(() => {
          this._store.dispatch(new VerifyEmailFetch());
          return this._profileService.verifyEmailRequest().pipe(
            map(res => {
              this._store.dispatch(new VerifyEmailRequestSuccess(res.detail));
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При верификации email возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new VerifyEmailFetchFailure(err.error));
              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  GetEmailTokenValidity$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<GetEmailTokenValidity>(EProfileActions.GetEmailTokenValidity),
        switchMap(payload => {
          return this._profileService.getEmailTokenValidity(payload.token).pipe(
            map(async () => {
              this._store.dispatch(
                new UpdateAuthUser({
                  ...payload.user,
                  is_email_confirmed: true,
                })
              );

              if (payload.user.id) {
                this._store.dispatch(
                  new EditSelfProfileRequest(payload.user.id, {
                    ...payload.user,
                    is_email_confirmed: true,
                  })
                );
                this._store.dispatch(new GetProfileRequest(payload.user.id));
              }

              this._toastr.success('Электронная почта успешно подтверждена');
              this._router.navigate(['profile']);
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При подтверждении почты возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._router.navigate(['profile']);
              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  VerifyPhone$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<VerifyPhone>(EProfileActions.VerifyPhone),
        switchMap(action => {
          return this._profileService.verifyPhone(action.code).pipe(
            map(() => {
              this._store.dispatch(
                new UpdateAuthUser({ is_phone_confirmed: true })
              );
              this._toastr.success('Номер телефона успешно подтвержден');
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При подтверждении телефона возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new VerifyPhoneRequestFailure(err.error));
              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  EditSelfProfile$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<EditSelfProfile>(EProfileActions.EditSelfProfile),
        switchMap(action => {
          return this._usersService.editUser(action.id, action.userData).pipe(
            map(() => {
              this._store.dispatch(new GetProfileRequest(action.id));
              this._toastr.success('Профиль успешно изменен!');
              this._router.navigate(['/profile'], {
                queryParams: { edit: false },
              });
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При изминении профиля возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new EditSelfProfileFailure(err.error));
              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  changePassword$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<ChangePassword>(EProfileActions.ChangePassword),
        switchMap(action => {
          return this._profileService.changePassword(action.passwordData).pipe(
            map(() => {
              this._store.dispatch(new ChangePasswordSuccess());
              this._toastr.success('Пароль успешно изменен!');
              this._router.navigate(['/profile'], {
                queryParams: { edit: false, password_edit: false },
              });
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При изминении пароля возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new ChangePasswordFailure(err.error));

              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );
}
